import { useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, FormField } from '@chikara/ui';
import { useActivateCardForm } from './useActivateCardForm';
import {
  CARD_MAX_LENGTH,
  STEP_DATA,
  STEP_LIMIT,
  STEP_CONFIRM,
  STEP_CONFIRMATION,
  STEP_FAILED,
} from './activateCard.constants';
import { lastFourDigits, formatCardNumber, formatCurrencyInput, hasDisplayFullName } from './activateCard.validators';
import { MfaModal } from '@/features/auth/components/MfaFlow/MfaModal';
import { ActivateCardModalProps } from './activateCard.types';
import * as ROUTES from '@/shared/constants/routes';
import styles from './ActivateCardModal.module.css';
import { t } from '@/i18n';


export function ActivateCardModal({
  isOpen,
  onClose,
  onSuccess,
}: ActivateCardModalProps) {
  const {
    state,
    handleCardNumberChange,
    handleConfirmCardNumberChange,
    handleSpendingLimitChange,
    handleSpendingLimitFocus,
    handleSpendingLimitBlur,
    handleNext,
    handleVerifyMfa,
    handleClose,
    handleBackToLimit,
    canGoNext,
    canActivate,
    isMfaOpen,
    setIsMfaOpen,
    openMfa,
    handleSuccess,
    isGlobalLoading,
  } = useActivateCardForm(onClose, isOpen, onSuccess);

  const navigate = useNavigate();
  const isClickInsideRef = useRef(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const handleMouseDown = (e: React.MouseEvent) => {
    isClickInsideRef.current = contentRef.current?.contains(e.target as Node) ?? false;
  };

  const handleOverlayClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (
        event.target === event.currentTarget &&
        !isClickInsideRef.current &&
        state.step !== STEP_CONFIRMATION &&
        !state.isLoading
      ) {
        handleClose();
      }
    },
    [handleClose, state.step, state.isLoading]
  );

  // Handle ESC key (disabled on step 3)
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && state.step !== STEP_CONFIRMATION) {
        handleClose();
      }
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen, state.step]);

  // Invitar a un nuevo usuario: cierra el modal y va a Equipo
  const handleInviteUser = useCallback(() => {
    handleClose();
    navigate(ROUTES.TEAM);
  }, [handleClose, navigate]);

  if (!isOpen) return null;

  // El diseño no muestra la X en el primer paso ni en el paso final de confirmación
  const showCloseButton =
    !state.isLoading && state.step !== STEP_DATA && state.step !== STEP_CONFIRMATION;

  const modalContent = (
    <div
      className={isGlobalLoading ? styles.overlayLoading : styles.overlay}
      onMouseDown={handleMouseDown}
      onClick={handleOverlayClick}
    >
      <div ref={contentRef} className={styles.content} onClick={(e) => e.stopPropagation()}>
        {showCloseButton && (
          <button
            type="button"
            className={styles.closeButton}
            onClick={handleClose}
            aria-label={t('dashboard.card.activateModal.closeLabel')}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        )}

        {state.isLoading && (
          <div className={styles.loadingPlaceholder} style={{ height: '300px' }} />
        )}

        {/* Step 1: Card Data */}
        {!state.isLoading && state.step === STEP_DATA && (
          <div className={styles.stepContent}>
            <h2 className={styles.title}>{t('dashboard.card.activateModal.step1.title')}</h2>
            <p className={styles.subtitle}>{t('dashboard.card.activateModal.step1.subtitle')}</p>

            <div className={styles.contextBlock}>
              <span className={styles.contextLabel}>
                {t('dashboard.card.activateModal.step1.contextLabel')}
              </span>
              <p className={styles.contextValue}>
                {hasDisplayFullName(state.fullName) && (
                  <span className={styles.contextName}>{state.fullName}</span>
                )}
                <span className={styles.contextEmail}>{state.email}</span>
              </p>
            </div>

            <div className={styles.selfActivationNotice}>
              <SelfActivationIcon />
              <span>{t('dashboard.card.activateModal.step1.selfActivationNotice')}</span>
            </div>

            <div className={styles.formFields}>
              <FormField
                label={t('dashboard.card.activateModal.step1.cardNumberLabel')}
                value={formatCardNumber(state.cardNumber)}
                onChange={handleCardNumberChange}
                error={state.errors['cardNumber']}
                maxLength={CARD_MAX_LENGTH}
                placeholder={t('dashboard.card.activateModal.step1.cardNumberPlaceholder')}
              />
              <FormField
                label={t('dashboard.card.activateModal.step1.confirmCardNumberLabel')}
                value={formatCardNumber(state.confirmCardNumber)}
                onChange={handleConfirmCardNumberChange}
                error={state.errors['confirmCardNumber']}
                maxLength={CARD_MAX_LENGTH}
                placeholder={t('dashboard.card.activateModal.step1.cardNumberPlaceholder')}
                onPaste={(e) => e.preventDefault()}
                onCopy={(e) => e.preventDefault()}
              />
            </div>

            <div className={styles.note}>
              <InfoIcon />
              <span>{t('dashboard.card.activateModal.step1.basicUsersNote')}</span>
            </div>

            <p className={styles.inviteLine}>
              {t('dashboard.card.activateModal.step1.invitePrompt')}{' '}
              <button
                type="button"
                className={styles.inviteLink}
                onClick={handleInviteUser}
              >
                {t('dashboard.card.activateModal.step1.inviteLink')}
              </button>
            </p>

            <div className={styles.actions}>
              <Button
                variant="primary"
                size="md"
                onClick={handleNext}
                disabled={!canGoNext}
                fullWidth
              >
                {t('dashboard.card.activateModal.step1.nextButton')}
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Manage Limit */}
        {!state.isLoading && state.step === STEP_LIMIT && (
          <div className={styles.stepContent}>
            <h2 className={styles.title}>{t('dashboard.card.activateModal.step2.title')}</h2>
            <p className={styles.subtitle}>
              {t('dashboard.card.activateModal.step2.subtitlePrefix', {
                lastFour: lastFourDigits(state.cardNumber),
              })}{' '}
              <span className={styles.subtitleEmail}>{state.email}</span>
              {t('dashboard.card.activateModal.step2.subtitleSuffix')}
            </p>

            <div className={styles.formFields}>
              <FormField
                label={t('dashboard.card.activateModal.step2.spendingLimitLabel')}
                value={state.isEditingLimit ? formatCurrencyInput(state.spendingLimit) : state.spendingLimitDisplay}
                onChange={handleSpendingLimitChange}
                onFocus={handleSpendingLimitFocus}
                onBlur={handleSpendingLimitBlur}
                error={state.errors['spendingLimit']}
              />
            </div>

            <div className={styles.actions}>
              <Button
                variant="primary"
                size="md"
                onClick={handleNext}
                disabled={!canActivate}
                fullWidth
              >
                {t('dashboard.card.activateModal.step2.activateButton')}
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Activate confirmation (summary) */}
        {!state.isLoading && state.step === STEP_CONFIRM && (
          <div className={styles.stepContent}>
            <h2 className={styles.title}>{t('dashboard.card.activateModal.stepConfirm.title')}</h2>
            <p className={styles.subtitle}>{t('dashboard.card.activateModal.stepConfirm.subtitle')}</p>

            <div className={styles.summary}>
              <div className={styles.summaryRow}>
                <span className={styles.summaryLabel}>
                  {t('dashboard.card.activateModal.stepConfirm.cardLabel')}
                </span>
                <span className={styles.summaryValue}>{lastFourDigits(state.cardNumber)}</span>
              </div>

              <div className={styles.summaryRow}>
                <span className={styles.summaryLabel}>
                  {t('dashboard.card.activateModal.stepConfirm.assignedLabel')}
                </span>
                <span className={styles.summaryValue}>
                  {hasDisplayFullName(state.fullName) && (
                    <span className={styles.summaryName}>{state.fullName}</span>
                  )}
                  <span className={styles.summaryEmail}>{state.email}</span>
                </span>
              </div>

              <div className={styles.summaryRow}>
                <span className={styles.summaryLabel}>
                  {t('dashboard.card.activateModal.stepConfirm.limitLabel')}
                </span>
                <span className={styles.summaryValue}>
                  {formatCurrencyInput(state.spendingLimit)}
                </span>
              </div>
            </div>

            <div className={`${styles.actions} ${styles.actionsRow}`}>
              <Button
                variant="outline"
                size="md"
                onClick={handleBackToLimit}
                disabled={isGlobalLoading}
                fullWidth
              >
                {t('dashboard.card.activateModal.stepConfirm.cancel')}
              </Button>
              <Button
                variant="primary"
                size="md"
                onClick={openMfa}
                fullWidth
              >
                {t('dashboard.card.activateModal.stepConfirm.confirm')}
              </Button>
            </div>
          </div>
        )}

        {/* Step 4: Confirmation */}
        {!state.isLoading && state.step === STEP_CONFIRMATION && (
          <div className={styles.stepContent}>
            <h2 className={styles.title}>
              {state.isPendingApproval
                ? t('dashboard.card.activateModal.step3.pendingTitle')
                : t('dashboard.card.activateModal.step3.title')}
            </h2>
            <p className={styles.confirmationText}>
              {state.isPendingApproval
                ? t('dashboard.card.activateModal.step3.pendingDescription')
                : t('dashboard.card.activateModal.step3.description')}
            </p>

            <div className={styles.actions}>
              <Button variant="primary" size="md" onClick={handleSuccess} fullWidth>
                {t('dashboard.card.activateModal.step3.confirmButton')}
              </Button>
            </div>
          </div>
        )}

        {/* Step 5: Failure */}
        {!state.isLoading && state.step === STEP_FAILED && (
          <div className={styles.stepContent}>
            <h2 className={styles.title}>
              {t('dashboard.card.activateModal.failure.title')}
            </h2>
            <p className={styles.confirmationText}>
              {t('dashboard.card.activateModal.failure.description')}
            </p>

            <div className={styles.actions}>
              <Button variant="primary" size="md" onClick={handleClose} fullWidth>
                {t('dashboard.card.activateModal.failure.button')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <>
      {modalContent}
      <MfaModal
        isOpen={isMfaOpen}
        onClose={() => setIsMfaOpen(false)}
        onVerify={handleVerifyMfa}
        isLoading={state.isLoading || isGlobalLoading}
      />
    </>
  );
}

// Aviso de activación propia (solo informativo, sin interacción)
function SelfActivationIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      className={styles.noticeIcon}
    >
      <rect x="1" y="1" width="14" height="14" rx="3.5" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M4.6 8.2L6.9 10.5L11.4 5.6"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// Icono informativo de la nota para usuarios básicos
function InfoIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      className={styles.noteIcon}
    >
      <circle cx="8" cy="8" r="6.75" stroke="currentColor" strokeWidth="1.5" />
      <path d="M8 7.2V11.2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <circle cx="8" cy="4.9" r="0.9" fill="currentColor" />
    </svg>
  );
}
